import { useMemo } from "react";
import { buildPeriodSlots } from "../../../Settings/Triggers/hooks/useTriggers";

const PERIOD_TYPE_LABELS = {
  YEARLY: "Yearly",
  HALF_YEARLY: "Half Yearly",
  QUARTERLY: "Quarterly",
  MONTHLY: "Monthly",
};

const PeriodSelector = ({
  fyLabel,
  startingMonth,
  periodType = "",
  selectedSlot = null,
  customFrom = "",
  customTo = "",
  onPeriodTypeChange,
  onSlotChange,
  onCustomFromChange,
  onCustomToChange,
}) => {
  const periodSlots = useMemo(() => {
    if (!fyLabel || !startingMonth || !periodType || periodType === "CUSTOM") return [];
    const allSlots = buildPeriodSlots(fyLabel, startingMonth);
    return allSlots[periodType] ?? [];
  }, [fyLabel, startingMonth, periodType]);

  const handleTypeChange = (e) => {
    onPeriodTypeChange?.(e.target.value);
    onSlotChange?.(null);
    onCustomFromChange?.("");
    onCustomToChange?.("");
  };

  const handleSlotChange = (e) => {
    const fromDate = e.target.value;
    if (!fromDate) { onSlotChange?.(null); return; }
    onSlotChange?.(periodSlots.find(s => s.fromDate === fromDate) ?? null);
  };

  const isActive = Boolean(periodType);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: "var(--ink-4)",
        textTransform: "uppercase", letterSpacing: "0.06em",
        fontFamily: "var(--font-sans)", whiteSpace: "nowrap",
      }}>
        Period
      </span>

      {/* Type select */}
      <select
        className={`kpi-inline-select${isActive ? " active" : ""}`}
        value={periodType}
        onChange={handleTypeChange}
        title="Period type"
      >
        <option value="">All periods</option>
        {Object.entries(PERIOD_TYPE_LABELS).map(([val, lbl]) => (
          <option key={val} value={val}>{lbl}</option>
        ))}
        <option value="CUSTOM">Custom range</option>
      </select>

      {/* Slot select — standard types */}
      {periodType && periodType !== "CUSTOM" && (
        <select
          className={`kpi-inline-select${selectedSlot ? " active" : ""}`}
          value={selectedSlot?.fromDate ?? ""}
          onChange={handleSlotChange}
          title="Select period"
        >
          <option value="">All {PERIOD_TYPE_LABELS[periodType]} periods</option>
          {periodSlots.map(s => (
            <option key={s.fromDate} value={s.fromDate}>{s.label}</option>
          ))}
        </select>
      )}

      {/* Custom date range */}
      {periodType === "CUSTOM" && (
        <>
          <input
            type="date"
            className="kpi-inline-select"
            value={customFrom}
            onChange={e => onCustomFromChange?.(e.target.value)}
            title="From date"
            style={{ width: 148 }}
          />
          <span style={{ fontSize: 11, color: "var(--ink-4)", flexShrink: 0 }}>→</span>
          <input
            type="date"
            className="kpi-inline-select"
            value={customTo}
            onChange={e => onCustomToChange?.(e.target.value)}
            title="To date"
            style={{ width: 148 }}
          />
        </>
      )}

      {/* Clear button */}
      {isActive && (
        <button
          type="button"
          onClick={() => {
            onPeriodTypeChange?.("");
            onSlotChange?.(null);
            onCustomFromChange?.("");
            onCustomToChange?.("");
          }}
          title="Clear period filter"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 26, height: 26,
            border: "1.5px solid var(--rule)",
            borderRadius: 6, background: "transparent",
            cursor: "pointer", fontSize: 13, color: "var(--ink-4)",
            lineHeight: 1, flexShrink: 0,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--rule)"; e.currentTarget.style.color = "var(--ink-4)"; }}
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default PeriodSelector;
